import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod/v3";
import { APIError } from "@/api/error";
import { useChangePassword } from "@/api/security";
import { Button } from "@/components/base/buttons/button";
import { PasswordInput } from "@/components/base/input/password-input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { globalHandleError } from "@/providers/QueryClientProviderWrapper";
import { success } from "@/providers/ToastProvider";

const MIN_LENGTH = 8;

const schema = z
  .object({ current: z.string(), next: z.string(), confirm: z.string() })
  .superRefine(({ current, next, confirm }, ctx) => {
    const issue = (path: string, message: string) => ctx.addIssue({ code: "custom", path: [path], message });

    if (!current.trim()) issue("current", "Enter your current password.");

    if (!next) issue("next", "Enter a new password.");
    else if (next.length < MIN_LENGTH) issue("next", `Use at least ${MIN_LENGTH} characters.`);
    else if (next === current) issue("next", "Choose a password that's different from your current one.");

    if (!confirm) issue("confirm", "Enter your new password again.");
    else if (next && confirm !== next) issue("confirm", "These don't match — re-enter your new password.");
  });

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = { current: "", next: "", confirm: "" };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onForgotPassword: () => void;
};

const ChangePasswordForm = ({ onClose, onForgotPassword }: { onClose: () => void; onForgotPassword: () => void }) => {
  const changePassword = useChangePassword();
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const submit = async ({ current, next }: FormValues) => {
    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
      onClose();
      success("Password updated", { description: "Use your new password the next time you sign in." });
    } catch (err) {
      const currentError =
        err instanceof APIError && err.isKind("FormValidationError") ? err.data.formErrors.currentPassword : undefined;
      if (currentError) form.setError("current", { message: currentError });
      else globalHandleError(err);
    }
  };

  const field = (name: keyof FormValues, label: string, placeholder: string, autoComplete: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <PasswordInput
          label={label}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isInvalid={!!fieldState.error}
          hint={fieldState.error?.message}
        />
      )}
    />
  );

  return (
    <form className="flex flex-col gap-5" onSubmit={form.handleSubmit(submit)} noValidate>
      <DialogTitle className="text-lg leading-7 font-semibold text-gray-900">Change your password</DialogTitle>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          {field("current", "Current password", "Enter your current password", "current-password")}
          <button
            type="button"
            onClick={onForgotPassword}
            className="mt-1.5 w-fit cursor-pointer text-[13px] font-medium text-brand-700 hover:text-brand-900 hover:underline"
          >
            Forgot password?
          </button>
        </div>
        {field("next", "New password", `At least ${MIN_LENGTH} characters`, "new-password")}
        {field("confirm", "Confirm new password", "Re-enter your new password", "new-password")}
      </div>
      <div className="flex gap-3">
        <Button type="button" color="secondary" size="lg" className="flex-1" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="lg" className="flex-1" isLoading={form.formState.isSubmitting}>
          Update password
        </Button>
      </div>
    </form>
  );
};

export const ChangePasswordDialog = ({ open, onOpenChange, onForgotPassword }: Props) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="rounded-xl bg-white sm:max-w-[440px]">
      <ChangePasswordForm onClose={() => onOpenChange(false)} onForgotPassword={onForgotPassword} />
    </DialogContent>
  </Dialog>
);
